import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { toast } from 'react-toastify';
import {
  FiArrowUpRight, FiCheck, FiEye, FiFileText, FiPaperclip, FiPause, FiRotateCcw, FiSearch, FiX,
} from 'react-icons/fi';
import { getUserName } from '../../../Redux/selecters';
import LeaseApiService from '../../services/apiService';
import {
  FilePreview, ProcPager, normalizeFile, pick,
} from './procurementFiles';
import './procurementApproval.css';

const PAGE_SIZE = 20;
const STATUS_OPTIONS = [
  ['All', 'All'], ['Pending', 'Awaiting decision'], ['Approved', 'Approved'], ['Hold', 'On hold'], ['Rejected', 'Rejected'],
];
const DOC_TYPE_OPTIONS = ['All', 'GRN', 'Purchase Order', 'Requisition', 'Issue'];
const DOT_COLORS = {
  grn: '#2F7F9E', 'purchase order': '#6B4BB0', requisition: '#B07A1F', issue: '#64748B',
};
// API takes dd-MMM-yy (e.g. 01-Sep-26); the inputs hold yyyy-MM-dd.
const API_DATE = 'DD-MMM-YY';
const todayStr = () => moment().format('YYYY-MM-DD');
const rangeStartStr = () => moment().subtract(29, 'days').format('YYYY-MM-DD');
const SUMMARY_TYPES = [['Requisition', 'Purchase Request'], ['Purchase Order', 'Purchase Order'], ['GRN', 'GRN'], ['Issue', 'Issue']];
const dotColor = (t) => DOT_COLORS[String(t).toLowerCase()] || '#64748B';
const statusLabel = (r) => (r.isApproved ? 'Approved' : r.isHold ? 'On hold' : r.isRejected ? 'Rejected' : 'Awaiting decision');

const normalizeRow = (row, index) => {
  const status = String(pick(row, ['status', 'approvalStatus'], '')).toLowerCase();
  return {
    ...row,
    // srNo is 1 on every row, so key on the document number instead.
    _key: pick(row, ['docno', 'docrefno', 'recordId', 'id'], index),
    docType: pick(row, ['doctype', 'docType', 'documentType', 'type']),
    docNo: pick(row, ['docno', 'docrefno', 'docNo', 'documentNo']),
    docDate: pick(row, ['docdate', 'docDate', 'date']),
    amount: pick(row, ['amount', 'totalAmount', 'docAmount', 'netAmount'], 0),
    isApproved: Boolean(Number(pick(row, ['approved', 'isApproved'], status === 'approved'))),
    isHold: Boolean(Number(pick(row, ['hold', 'isHold'], status === 'hold'))),
    isRejected: Boolean(Number(pick(row, ['rejected', 'isRejected'], status === 'rejected'))),
    remarks: pick(row, ['remarks', 'approvalRemarks'], ''),
    user: pick(row, ['userName', 'user', 'createdBy', 'requestedBy']),
    attachments: pick(row, ['attachments', 'attachmentCount', 'fileCount'], null),
  };
};

const ProcurementApproval = () => {
  const apiService = useMemo(() => new LeaseApiService(), []);
  const userName = useSelector(getUserName);
  const navigate = useNavigate();

  const [docNo, setDocNo] = useState('');
  const [filter, setFilter] = useState('All');
  const [docType, setDocType] = useState('All');
  const [fromDate, setFromDate] = useState(rangeStartStr());
  const [toDate, setToDate] = useState(todayStr());
  const [rowData, setRowData] = useState([]);
  const [pagination, setPagination] = useState();
  const [total, setTotal] = useState(0);
  const [filesModal, setFilesModal] = useState(null); // { row, loading, files }
  const [preview, setPreview] = useState(null);

  // The list has no attachment count, so ask the files endpoint once per row.
  const loadFileCounts = (rows) => {
    rows.forEach(async (row) => {
      try {
        const params = new URLSearchParams();
        params.append('DocNo', row.docNo);
        params.append('DocType', String(row.docType).replace(/\s+/g, ''));
        const res = await apiService.getApiParamater('GET_PROCUREMENT_DOC_FILES', `?${params.toString()}`);
        const body = res?.data;
        const files = Array.isArray(body) ? body : body?.result || body?.data || [];
        setRowData((prev) => prev.map((r) => (r._key === row._key ? { ...r, attachments: files.length } : r)));
      } catch (e) {
        /* leave the count blank for this row */
      }
    });
  };

  const fetchList = async (pageNo = 1, overrides = {}) => {
    const f = { docNo, filter, fromDate, toDate, ...overrides };
    try {
      const params = new URLSearchParams();
      params.append('UserName', userName || '');
      params.append('From', moment(f.fromDate).format(API_DATE));
      params.append('To', moment(f.toDate).format(API_DATE));
      params.append('Filter', f.filter);
      params.append('DocNo', f.docNo);
      params.append('PageNo', pageNo);
      params.append('PageSize', PAGE_SIZE);
      const res = await apiService.getApiParamater('GET_PROCUREMENT_APPROVAL', `?${params.toString()}`);
      const body = res?.data;
      const list = Array.isArray(body) ? body : body?.result || body?.data || [];
      const pg = body?.paginationData || body?.pagination;
      const normalized = list.map(normalizeRow);
      setRowData(normalized);
      loadFileCounts(normalized);
      setPagination(pg);
      setTotal(pg?.totalRecords ?? pg?.totalCount ?? pg?.total ?? list.length);
    } catch (error) {
      console.error('Error fetching procurement approvals:', error);
      toast.error('Unable to load procurement approvals.');
      setRowData([]);
    }
  };

  useEffect(() => {
    if (userName) fetchList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userName]);

  // Esc closes the attachments popup (the viewer handles its own Esc).
  useEffect(() => {
    if (!filesModal || preview !== null) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setFilesModal(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [filesModal, preview]);

  const openAttachments = async (row) => {
    setFilesModal({ row, loading: true, files: [] });
    try {
      const params = new URLSearchParams();
      params.append('DocNo', row.docNo);
      params.append('DocType', String(row.docType).replace(/\s+/g, ''));
      const res = await apiService.getApiParamater('GET_PROCUREMENT_DOC_FILES', `?${params.toString()}`);
      const body = res?.data;
      const list = Array.isArray(body) ? body : body?.result || body?.data || [];
      setFilesModal({ row, loading: false, files: list.map(normalizeFile) });
    } catch (error) {
      console.error('Error fetching attachments:', error);
      toast.error('Unable to load attachments.');
      setFilesModal(null);
    }
  };

  const handleReset = () => {
    const reset = { docNo: '', filter: 'All', fromDate: rangeStartStr(), toDate: todayStr() };
    setDocNo(reset.docNo);
    setFilter(reset.filter);
    setDocType('All');
    setFromDate(reset.fromDate);
    setToDate(reset.toDate);
    fetchList(1, reset);
  };

  // Approve / Hold / Reject behave like one choice per row; clicking the active one clears it.
  const setDecision = (key, field) => {
    setRowData((prev) => prev.map((row) => (
      row._key === key
        ? { ...row, isApproved: false, isHold: false, isRejected: false, [field]: !row[field] }
        : row
    )));
  };

  const setRemark = (key, value) => {
    setRowData((prev) => prev.map((row) => (row._key === key ? { ...row, remarks: value } : row)));
  };

  const visibleRows = useMemo(
    () => (docType === 'All' ? rowData : rowData.filter((r) => String(r.docType).toLowerCase() === docType.toLowerCase())),
    [rowData, docType],
  );

  const stats = useMemo(() => {
    const approved = visibleRows.filter((r) => r.isApproved);
    return {
      awaiting: visibleRows.filter((r) => !r.isApproved && !r.isHold && !r.isRejected).length,
      approved: approved.length,
      approvedValue: approved.reduce((sum, r) => sum + (Number(r.amount) || 0), 0),
      hold: visibleRows.filter((r) => r.isHold).length,
      rejected: visibleRows.filter((r) => r.isRejected).length,
      byType: SUMMARY_TYPES.map(([type, label]) => {
        const rows = visibleRows.filter((r) => String(r.docType).toLowerCase() === type.toLowerCase());
        return {
          type,
          label,
          total: rows.length,
          pending: rows.filter((r) => !r.isApproved && !r.isHold && !r.isRejected).length,
          approved: rows.filter((r) => r.isApproved).length,
        };
      }),
    };
  }, [visibleRows]);

  const rangeLabel = useMemo(() => {
    const from = moment(fromDate);
    const to = moment(toDate);
    if (!from.isValid() || !to.isValid()) return '';
    const range = `${from.format('D MMM')} to ${to.format('D MMM YYYY')}`;
    return to.diff(from, 'days') === 29 ? `Last 30 days · ${range}` : range;
  }, [fromDate, toDate]);

  const modalRow = filesModal?.row;
  const viewable = (filesModal?.files || []).filter((f) => f.href);

  return (
    <div className="proc-page">
      <div className="proc-title">
        <h1>Approvals</h1>
        <p>Review procurement documents and record a decision on each one.</p>
      </div>

      <section className="proc-stats" aria-label="Decision summary">
        <div className="proc-stat-hero">
          <div className="proc-hero-top">
            <span className="proc-hero-label">Pending approval</span>
            <span className="proc-hero-range">Last 30 days</span>
          </div>
          <div className="proc-hero-main">
            <span className="proc-hero-value">{stats.awaiting}</span>
            <span className="proc-hero-of">of {visibleRows.length} documents</span>
          </div>
          <div className="proc-hero-legend">
            <span><i className="dot" style={{ background: '#6FD3A0' }} />Approved {stats.approved}</span>
            <span><i className="dot" style={{ background: '#F2C45A' }} />On hold {stats.hold}</span>
            <span><i className="dot" style={{ background: '#F28B82' }} />Rejected {stats.rejected}</span>
          </div>
        </div>
        {stats.byType.map((t) => (
          <div className="proc-stat" key={t.type}>
            <div className="proc-stat-label"><i className="sq" style={{ background: dotColor(t.type) }} />{t.label}</div>
            <div className="proc-stat-main"><span className="proc-stat-value">{t.pending}</span><span className="proc-stat-pending">pending</span></div>
            <div className="proc-stat-note proc-mono">{t.total} total · {t.approved} approved</div>
          </div>
        ))}
      </section>

      <section className="proc-panel proc-filters" aria-label="Approval filters">
        <div className="proc-filter-grid">
          <div className="proc-field">
            <label htmlFor="f-doc">Document no</label>
            <input
              id="f-doc"
              className="proc-input"
              placeholder="e.g. PO/124691"
              value={docNo}
              onChange={(e) => setDocNo(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchList(1)}
            />
          </div>
          <div className="proc-field">
            <label htmlFor="f-status">Status</label>
            <select id="f-status" className="proc-input" value={filter} onChange={(e) => setFilter(e.target.value)}>
              {STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div className="proc-field">
            <label htmlFor="f-type">Document type</label>
            <select id="f-type" className="proc-input" value={docType} onChange={(e) => setDocType(e.target.value)}>
              {DOC_TYPE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div className="proc-field">
            <label htmlFor="f-from">From date</label>
            <input id="f-from" type="date" className="proc-input" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </div>
          <div className="proc-field">
            <label htmlFor="f-to">To date</label>
            <input id="f-to" type="date" className="proc-input" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>
        </div>
        <div className="proc-filter-actions">
          <button type="button" className="proc-btn proc-btn-ghost" onClick={handleReset}><FiRotateCcw size={16} />Reset</button>
          <button type="button" className="proc-btn proc-btn-primary" onClick={() => fetchList(1)}><FiSearch size={16} />Search</button>
        </div>
      </section>

      <section className="proc-panel proc-list" aria-label="Approval list">
        <div className="proc-list-head">
          <h2>Approval list</h2>
          <span>{rangeLabel}</span>
        </div>
        <div className="proc-table-wrap">
          <table className="proc-table">
            <thead>
              <tr>
                <th scope="col">Doc type</th>
                <th scope="col">Doc no</th>
                <th scope="col">Date</th>
                <th scope="col" className="num">Amount</th>
                <th scope="col">Files</th>
                <th scope="col">Decision</th>
                <th scope="col">Higher authority</th>
                <th scope="col">Remarks</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => {
                const amount = Number(row.amount) || 0;
                const count = row.attachments === null || row.attachments === undefined ? null : Number(row.attachments) || 0;
                return (
                  <tr key={row._key}>
                    <td>
                      <span className="proc-type"><i className="sq" style={{ background: dotColor(row.docType) }} />{row.docType}</span>
                    </td>
                    <td><span className="proc-docno">{row.docNo}</span></td>
                    <td className="proc-mono proc-sub">{row.docDate}</td>
                    <td className="num proc-mono"><span className={amount === 0 ? 'zero' : ''}>{amount.toFixed(3)}</span></td>
                    <td>
                      <button
                        type="button"
                        className={`proc-clip${count ? ' has' : ''}`}
                        aria-label={`View attachments for ${row.docNo}`}
                        onClick={() => openAttachments(row)}
                      >
                        <FiPaperclip size={16} />{count !== null && <span className="proc-mono">{count}</span>}
                      </button>
                    </td>
                    <td>
                      <div className="proc-seg" role="group" aria-label={`Decision for ${row.docNo}`}>
                        <button type="button" className={`dec-approve${row.isApproved ? ' on' : ''}`} aria-pressed={row.isApproved} onClick={() => setDecision(row._key, 'isApproved')}><FiCheck size={14} />Approve</button>
                        <button type="button" className={`dec-hold${row.isHold ? ' on' : ''}`} aria-pressed={row.isHold} onClick={() => setDecision(row._key, 'isHold')}><FiPause size={14} />Hold</button>
                        <button type="button" className={`dec-reject${row.isRejected ? ' on' : ''}`} aria-pressed={row.isRejected} onClick={() => setDecision(row._key, 'isRejected')}><FiX size={14} />Reject</button>
                      </div>
                    </td>
                    <td>
                      {row.isApproved && String(row.docType).toLowerCase() === 'purchase order' && (
                        <button
                          type="button"
                          className="proc-escalate"
                          onClick={() => toast.info(`Send to top authority: ${row.docNo} (action endpoint not configured yet)`)}
                        >
                          Send to top authority <FiArrowUpRight size={13} />
                        </button>
                      )}
                    </td>
                    <td>
                      <input
                        className="proc-remark"
                        type="text"
                        placeholder="Add a remark"
                        aria-label={`Remark for ${row.docNo}`}
                        value={row.remarks}
                        onChange={(e) => setRemark(row._key, e.target.value)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {visibleRows.length === 0 && (
          <div className="proc-empty">No documents match these filters. Reset the filters to see the full list.</div>
        )}
        <div className="proc-list-foot">
          <ProcPager pagination={pagination} onPage={(n) => fetchList(n)} />
          <div className="proc-showing">Showing <span className="proc-mono">{visibleRows.length}</span> of <span className="proc-mono">{total}</span> documents</div>
        </div>
      </section>

      {filesModal && (
        <div className="proc-modal-wrap">
          <button type="button" tabIndex={-1} aria-label="Close attachments" className="proc-modal-scrim" onClick={() => setFilesModal(null)} />
          <div role="dialog" aria-modal="true" aria-label="Attachments" className="proc-modal">
            <div className="proc-modal-head">
              <div>
                <h2>Attachments</h2>
                <div className="proc-modal-sub">
                  <span className="proc-docno">{modalRow.docNo}</span>
                  <i className="sep" />
                  <span>{modalRow.docType}</span>
                </div>
              </div>
              <button type="button" className="proc-icon-btn" aria-label="Close" onClick={() => setFilesModal(null)}><FiX size={16} /></button>
            </div>
            <div className="proc-modal-body">
              {filesModal.loading && <div className="proc-modal-empty">Loading…</div>}
              {!filesModal.loading && filesModal.files.length === 0 && <div className="proc-modal-empty">This document has no attachments.</div>}
              {!filesModal.loading && filesModal.files.length > 0 && (
                <div className="proc-modal-table">
                  <table className="proc-table" style={{ minWidth: 460 }}>
                    <thead><tr><th scope="col">File name</th><th scope="col">Remarks</th><th scope="col" className="num">View</th></tr></thead>
                    <tbody>
                      {filesModal.files.map((f) => (
                        <tr key={f.key}>
                          <td className="proc-breakall"><span className="proc-file-inline"><FiFileText size={18} color="#6B6880" />{f.name}</span></td>
                          <td className="proc-sub">{f.remarks}</td>
                          <td className="num">
                            <button type="button" className="proc-view-btn" disabled={!f.href} onClick={() => setPreview(f.key)}><FiEye size={16} />View</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="proc-modal-foot">
              <button
                type="button"
                className="proc-linkaction"
                onClick={() => navigate('/Lease/Management/Procurement/Approval/Attachments', { state: { row: modalRow } })}
              >
                Open attachments page <FiArrowUpRight size={14} />
              </button>
              <button type="button" className="proc-btn proc-btn-primary" onClick={() => setFilesModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {filesModal && preview !== null && (
        <FilePreview
          files={viewable}
          startKey={preview}
          onClose={() => setPreview(null)}
          doc={{
            docNo: modalRow.docNo, docType: modalRow.docType, docDate: modalRow.docDate, amount: modalRow.amount, status: statusLabel(modalRow),
          }}
        />
      )}
    </div>
  );
};

export default ProcurementApproval;
